// common/hooks/useReservationChartData.js

import { useState, useEffect } from "react";
import { apiRequest } from "common/utils/api";
import { REQUEST_METHOD } from "common/enum/RequestMethod";
import { showMessageModal } from "common/utils/modalController";
import { formatDateTime } from "common/utils/dateUtil";

function calculateTeeTimePlayersByCourse(data, startDate, endDate) {
  const rangeStart = new Date(startDate);
  const rangeEnd = new Date(endDate);

  const toMinutes = (time) => {
    const [h, m, s] = time.split(":").map(Number);
    return h * 60 + m + (s ? s / 60 : 0);
  };

  const toTimeString = (minutes) => {
    const h = String(Math.floor(minutes / 60)).padStart(2, "0");
    const m = String(Math.floor(minutes % 60)).padStart(2, "0");
    return `${h}:${m}`;
  };

  const getDaysBetween = (start, end) => {
    const days = [];
    const cur = new Date(start);
    cur.setHours(0, 0, 0, 0);
    end = new Date(end);
    end.setHours(0, 0, 0, 0);
    while (cur.getTime() <= end.getTime()) {
      const y = cur.getFullYear();
      const m = String(cur.getMonth() + 1).padStart(2, "0");
      const d = String(cur.getDate()).padStart(2, "0");
      days.push(`${y}-${m}-${d}`);
      cur.setDate(cur.getDate() + 1);
    }
    return days;
  };

  const result = {};
  data.forEach((item) => {
    const itemStart = new Date(item.startDate);
    const itemEnd = new Date(item.endDate);
    const start = itemStart < rangeStart ? rangeStart : itemStart;
    const end = itemEnd > rangeEnd ? rangeEnd : itemEnd;
    if (start > end) return;

    const first = toMinutes(item.firstTeeTime);
    const last = toMinutes(item.lastTeeTime);
    const interval = item.timeIntervalMin;

    const teeTimes = [];
    for (let t = first; t <= last; t += interval)
      teeTimes.push(toTimeString(t));

    getDaysBetween(start, end).forEach((dateKey) => {
      if (!result[dateKey]) result[dateKey] = {};
      if (!result[dateKey][item.courseId]) result[dateKey][item.courseId] = {};
      teeTimes.forEach((tt) => {
        if (!result[dateKey][item.courseId][tt])
          result[dateKey][item.courseId][tt] = 0;
        result[dateKey][item.courseId][tt] += item.maxPlayers;
      });
    });
  });
  return result;
}

function calculateCourseTotal(playersByCourse) {
  const courseTotals = {};
  Object.values(playersByCourse).forEach((dayData) => {
    Object.entries(dayData).forEach(([courseId, teeTimes]) => {
      if (!courseTotals[courseId]) courseTotals[courseId] = 0;
      courseTotals[courseId] += Object.values(teeTimes).reduce(
        (sum, val) => sum + val,
        0,
      );
    });
  });
  return courseTotals;
}

function summarizeReservationsByWeekday(data) {
  const weekdayMap = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
  const grouped = { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0 };
  data.forEach(({ reservationDate, reservationCount }) => {
    const weekday = weekdayMap[new Date(reservationDate).getDay()];
    grouped[weekday] += Number(reservationCount) || 0;
  });
  return weekdayMap
    .slice(1)
    .concat(["sun"])
    .map((day) => ({ key: day, value: grouped[day] || 0 }));
}

function summarizeReservationsForChart(data) {
  const grouped = {};
  data.forEach(({ courseId, teeTime, reservationCount }) => {
    const time = teeTime.slice(0, 5);
    if (!grouped[courseId]) grouped[courseId] = {};
    grouped[courseId][time] =
      (grouped[courseId][time] || 0) + (Number(reservationCount) || 0);
  });
  const allTeeTimes = Array.from(
    new Set(data.map((d) => d.teeTime.slice(0, 5))),
  ).sort((a, b) => a.localeCompare(b));
  return Object.entries(grouped).map(([courseId, times]) => ({
    id: Number(courseId),
    data: allTeeTimes.map((time) => ({ key: time, value: times[time] ?? 0 })),
  }));
}

export function mergeCoursesByTeeTime(courseDataArray) {
  const teeTimeSum = {};
  courseDataArray?.forEach(({ data }) => {
    data?.forEach(({ key, value }) => {
      teeTimeSum[key] = (teeTimeSum[key] || 0) + value;
    });
  });
  return [
    {
      id: "all",
      data: Object.entries(teeTimeSum)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([key, value]) => ({ key, value })),
    },
  ];
}

export const useReservationChartData = (startDate, endDate) => {
  const clubId = sessionStorage.getItem("clubId");

  const [courses, setCourses] = useState([]);
  const [summaryData, setSummaryData] = useState({
    Reservations: 0,
    Players: 0,
    MaxReservations: 0,
  });
  const [reservationByCourseData, setReservationByCourseData] = useState({
    max: 0,
    data: [],
  });
  const [areaChartData, setAreaChartData] = useState([
    { id: "Reservations", data: [] },
  ]);
  const [filteredAreaChartData, setFilteredAreaChartData] = useState([]);
  const [barChartData, setBarChartData] = useState([
    { key: "mon", value: 0 },
    { key: "tue", value: 0 },
    { key: "wed", value: 0 },
    { key: "thu", value: 0 },
    { key: "fri", value: 0 },
    { key: "sat", value: 0 },
    { key: "sun", value: 0 },
  ]);

  useEffect(() => {
    if (!startDate || !endDate) return;

    const fetch = async () => {
      try {
        const res = await apiRequest({
          method: REQUEST_METHOD.GET,
          url: `/api/v1.0/stats/reservations`,
          queryParameter: { clubId, startDate, endDate },
        });

        if (res.data?.code === "0000") {
          const { reservationByTimes, teeTimeInfo, totals, courses } =
            res.data.stats;

          const sumTotals = totals?.reduce(
            (acc, cur) => {
              acc.totalPlayers += cur.totalPlayers;
              acc.totalReservations += cur.totalReservations;
              return acc;
            },
            { totalPlayers: 0, totalReservations: 0 },
          );

          const playersByCourse = calculateTeeTimePlayersByCourse(
            teeTimeInfo,
            startDate,
            endDate,
          );
          const maxPlayersByCourse = calculateCourseTotal(playersByCourse);
          const maxReservations = Object.values(maxPlayersByCourse).reduce(
            (sum, val) => sum + val,
            0,
          );

          setSummaryData({
            Reservations: sumTotals?.totalReservations || 0,
            Players: sumTotals?.totalPlayers || 0,
            MaxReservations: maxReservations,
          });

          let reservationSum = 0;
          const totalByCourse = courses?.map((course) => {
            const totalReservations =
              totals?.find((t) => t.courseId === course.id)
                ?.totalReservations ?? 0;
            reservationSum += totalReservations;
            return {
              key: course.id,
              name: course.courseName,
              value: totalReservations,
            };
          });

          setCourses(
            courses?.map((course) => ({
              id: course.id,
              name: course.courseName,
            })),
          );
          setReservationByCourseData({
            max: reservationSum,
            data: totalByCourse,
          });

          const chartData = summarizeReservationsForChart(reservationByTimes);
          setAreaChartData(chartData);
          setFilteredAreaChartData(mergeCoursesByTeeTime(chartData));
          setBarChartData(summarizeReservationsByWeekday(reservationByTimes));
        } else if (res.data?.message) {
          showMessageModal(res.data.message);
        }
      } catch (e) {
        console.error(e);
        showMessageModal(e.response?.data?.message);
      }
    };

    fetch();
  }, [startDate, endDate]);

  return {
    courses,
    summaryData,
    reservationByCourseData,
    areaChartData,
    filteredAreaChartData,
    setFilteredAreaChartData,
    barChartData,
  };
};
